import { X, AlertTriangle, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ValidationErrorItem {
  message: string;
  deviceType: 'controller' | 'doublecheck' | 'zone';
  deviceId: string;
  controllerId?: string;
  zoneNumber?: number;
}

interface InspectionReportValidationErrorProps {
  isOpen: boolean;
  errors: ValidationErrorItem[];
  onClose: () => void;
  onErrorClick: (error: ValidationErrorItem) => void;
}

export function InspectionReportValidationError({
  isOpen,
  errors,
  onClose,
  onErrorClick,
}: InspectionReportValidationErrorProps) {
  const { t } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col animate-scale-in">
        <div className="px-6 py-4 border-b border-line flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="text-red-600 dark:text-red-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-theme-primary">{t('validationError.title')}</h3>
              <p className="text-sm text-theme-secondary">{t('validationError.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-theme-secondary hover:text-theme-primary transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          <p className="text-sm text-theme-primary mb-4 font-medium">
            {t('validationError.instruction')}
          </p>
          <div className="space-y-2">
            {errors.map((error, index) => (
              <button
                key={index}
                onClick={() => {
                  onErrorClick(error);
                  onClose();
                }}
                className="w-full flex items-start gap-3 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors text-left group"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">
                  {index + 1}
                </div>
                <p className="text-sm text-red-800 dark:text-red-300 flex-1 leading-relaxed">{error.message}</p>
                <ChevronRight size={18} className="flex-shrink-0 text-red-400 group-hover:text-red-600 transition-colors mt-0.5" />
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 bg-subtle border-t border-line rounded-b-xl flex items-center justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            {tCommon('actions.ok')}
          </button>
        </div>
      </div>
    </div>
  );
}
